import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, Phone, Clock, MapPin } from "lucide-react";

import { breadcrumbJsonLd, seo } from "@/lib/seo";
import { localBusinessSchema, NAP, SHOW_PHONE } from "@/lib/business";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Aurora } from "@/components/Aurora";
import { LeadForm } from "@/components/forms/LeadForm";

export const Route = createFileRoute("/_site/contact")({
  head: () =>
    seo({
      title: "Contact an AI Consultant in Las Vegas, NV",
      description:
        "Talk to LAA Concierge Consulting about AI in your business. Based in downtown Las Vegas, working with small businesses across Nevada and nationwide.",
      path: "/contact",
      jsonLd: [localBusinessSchema, breadcrumbJsonLd([{ name: "Contact", path: "/contact" }])],
    }),
  component: Page,
});

const INTEREST_OPTIONS = [
  "AI Opportunity Assessment",
  "AI Implementation",
  "Workflow Automation",
  "AI Enablement & Training",
  "Business funding (SBA, grants, business plans)",
  "Not sure yet",
] as const;

function Page() {
  return (
    <Section tone="paper" className="relative overflow-hidden">
      <Aurora variant="light" className="opacity-70" />
      <Container className="relative">
        <div className="grid gap-14 min-[900px]:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] min-[900px]:gap-16">
          <div>
            <h1 className="font-display text-display-lg text-ink-900 text-balance">
              Talk to us about AI in your business.
            </h1>
            <p className="mt-5 measure text-body-lg text-ink-600">
              Tell us what you're trying to fix and we'll tell you whether AI helps. If it doesn't,
              we'll say so.
            </p>

            <div className="mt-10">
              <LeadForm
                formType="contact"
                interestOptions={INTEREST_OPTIONS}
                submitLabel="Send message"
                onSuccessRedirect="/thank-you?f=contact"
              />
            </div>
          </div>

          <aside className="min-[900px]:pt-2">
            <div className="rounded-2xl border border-ink-200 bg-paper-0 p-7">
              <h2 className="font-display text-h3 text-ink-900">LAA Concierge Consulting</h2>

              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-3">
                  <MapPin
                    size={20}
                    strokeWidth={1.5}
                    className="mt-0.5 shrink-0 text-brand-600"
                    aria-hidden="true"
                  />
                  <span className="text-body text-ink-600">
                    {NAP.street}
                    <br />
                    {NAP.city}, {NAP.region} {NAP.postalCode}
                    <br />
                    <span className="mt-2 block text-body-sm text-ink-500">
                      In person across the Las Vegas valley, and remotely with small businesses
                      elsewhere in Nevada and nationwide.
                    </span>
                  </span>
                </li>
                {SHOW_PHONE ? (
                  <li className="flex items-start gap-3">
                    <Phone
                      size={20}
                      strokeWidth={1.5}
                      className="mt-0.5 shrink-0 text-brand-600"
                      aria-hidden="true"
                    />
                    <a
                      href={NAP.phoneHref}
                      className="inline-flex min-h-[44px] items-center text-body text-brand-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                    >
                      {NAP.phone}
                    </a>
                  </li>
                ) : null}
                <li className="flex items-start gap-3">
                  <Mail
                    size={20}
                    strokeWidth={1.5}
                    className="mt-0.5 shrink-0 text-brand-600"
                    aria-hidden="true"
                  />
                  <a
                    href={`mailto:${NAP.email}`}
                    className="inline-flex min-h-[44px] items-center text-body text-brand-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                  >
                    {NAP.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Clock
                    size={20}
                    strokeWidth={1.5}
                    className="mt-0.5 shrink-0 text-brand-600"
                    aria-hidden="true"
                  />
                  <span className="text-body text-ink-600">{NAP.hours}</span>
                </li>
              </ul>
            </div>

            <div className="mt-6 rounded-2xl border border-ink-200 bg-paper-0 p-7">
              <p className="text-body text-ink-600">Prefer to just talk?</p>
              <Link
                to="/book"
                className="mt-2 inline-flex min-h-[44px] items-center text-body-lg font-medium text-brand-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
              >
                Book a free 20-minute AI call
              </Link>
            </div>
          </aside>
        </div>
      </Container>
    </Section>
  );
}
